/* eslint-disable no-console */
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useAuthenticatedUser, useViewerRoles } from '@treams/treams-hooks';
import { DeleteIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Heading3,
	IconButton,
	Paragraph,
	Select,
	Separator,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import { UserSelector } from '../../users/UserCollaboratorsSelector';

interface IProps {
	group: { id: string };
}

export default function MembersTable({ group }: IProps): ReactElement {
	const { t } = useTranslation();
	const authenticatedUser = useAuthenticatedUser();

	const { data, loading, refetch } = graphql.useGroupMembersQuery({
		variables: {
			id: group.id,
		},
	});

	const [optimisticMembers, setOptimisticMembers] = useState<graphql.GroupMemberEdge[]>([]);
	const [loadingUserID, setLoadingUserID] = useState<string | undefined>(undefined);

	useEffect(() => {
		setOptimisticMembers((data?.group.members.edges as graphql.GroupMemberEdge[]) ?? []);
	}, [data]);

	const { data: rolesData } = graphql.useRolesQuery({
		variables: {
			target: graphql.RoleTargetType.Team,
		},
	});

	const [assignRole] = graphql.useAssignRoleMutation();

	const addUser = async (user: graphql.User) => {
		setOptimisticMembers([
			...optimisticMembers,
			{
				node: {
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
				},
				role: {
					id: 'group-member',
					type: graphql.RoleType.Member,
				},
			} as graphql.GroupMemberEdge,
		]);

		updateUser(user.id!, 'group-member');
	};

	const updateUser = async (id: string, roleID: string) => {
		setLoadingUserID(id);

		await assignRole({
			variables: {
				input: {
					userID: id,
					roleID,
					targetType: graphql.RoleTargetType.Team,
					targetID: group.id,
					isIndirect: false,
				},
			},
		});

		await refetch();

		setLoadingUserID(undefined);
	};

	const [revokeRole] = graphql.useRevokeRoleMutation();

	const deleteUser = async (id: string) => {
		setLoadingUserID(id);

		await revokeRole({
			variables: {
				input: {
					userID: id,
					targetType: graphql.RoleTargetType.Team,
					targetID: group.id,
					roleID: 'group-member',
				},
			},
		});

		await refetch();

		setLoadingUserID(undefined);
	};

	const isSuperUser = useViewerRoles().isWorkspaceSuperuser;

	if (loading) {
		return <Spinner centered />;
	}

	const ownerID = data?.group.members.edges.find(
		(edge) => edge.node.id === authenticatedUser.id && edge.role.type === graphql.RoleType.Owner,
	)?.node.id;

	const isOwner = authenticatedUser.id === ownerID || isSuperUser;

	return (
		<>
			<Separator />
			<Flex alignItems="center" mt={`${tokens.gap.section}px`}>
				<Box mr="10px">
					<Heading3>{t('common.settings.teams.create.members')}</Heading3>
				</Box>
			</Flex>

			<Separator mt={3} />

			<Flex flexDirection="column">
				{optimisticMembers
					.map(
						(edge) =>
							[
								edge,
								`${edge.node.id === ownerID ? '0' : '1'}-${edge.node.firstName} ${
									edge.node.lastName
								}`,
							] as [graphql.GroupMemberEdge, string],
					)
					.sort((a, b) => a[1].localeCompare(b[1]))
					.map(([edge]) => {
						const fullName = `${edge.node.firstName} ${edge.node.lastName}`;

						return (
							<Flex key={edge.node.id} flexDirection="column">
								<Box py={2} px={3}>
									<Flex flexDirection="row" justifyContent="space-between">
										<Flex flexDirection="row" alignSelf="center">
											<Box mr={3}>
												<Avatar user={edge.node} size={24} />
											</Box>
											<Paragraph>{fullName}</Paragraph>
										</Flex>
										<Box alignSelf="center" mx={5} flex={2} />
										<Box
											alignSelf="center"
											ml={2}
											flex="200px"
											flexShrink={0}
											flexGrow={0}
											width={200}
										>
											{isOwner && ownerID !== edge.node.id ? (
												<Select
													key={edge.role.id}
													disabled={!!loadingUserID}
													small
													selectedValue={edge.role.id}
													options={rolesData?.roles
														.map((role) => ({
															title: t(`roles.${role.id}`),
															value: role.id,
														}))
														.reverse()}
													onChange={(value) => updateUser(edge.node.id, value!)}
												/>
											) : (
												t(`roles.${edge.role.id}`)
											)}
										</Box>
										<Box alignSelf="center" ml={4} width={32}>
											{isOwner &&
												ownerID !== edge.node.id &&
												(loadingUserID === edge.node.id ? (
													<Spinner />
												) : (
													!loadingUserID && (
														<IconButton
															icon={<DeleteIcon />}
															onClick={() => deleteUser(edge.node.id)}
															type="destructive"
														/>
													)
												))}
										</Box>
									</Flex>
								</Box>

								<Separator />
							</Flex>
						);
					})}

				{isOwner && (
					<Box p={3}>
						<UserSelector
							selectedUsers={optimisticMembers.map((edge) => edge.node) as any}
							onSelectUser={(user) => addUser(user!)}
							small
						/>
					</Box>
				)}
			</Flex>
		</>
	);
}
